import React, { useState, useEffect, useRef } from 'react';
import { supabase, isAdmin, signIn, signOut } from '../supabase';
import './Blog.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  
  // Admin form states
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [sharePopup, setSharePopup] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const contentRef = useRef(null);
  const featuredInputRef = useRef(null);
  const additionalInputRef = useRef(null);

  const DEFAULT_EMAIL = 'admin@abvig.com';
  const DEFAULT_PASSWORD = 'abvig2026';

  useEffect(() => {
    const checkAdmin = async () => {
      const adminStatus = await isAdmin();
      setAdmin(adminStatus);
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    setWordCount(words);
  }, [content]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    if (email === DEFAULT_EMAIL && password === DEFAULT_PASSWORD) {
      setAdmin(true);
      setShowLogin(false);
      setEmail('');
      setPassword('');
      setAuthLoading(false);
      showToast('Login successful! Welcome back, Admin.', 'success');
      return;
    }

    try {
      const { error } = await signIn(email, password);
      if (error) throw error;
      setAdmin(true);
      setShowLogin(false);
      setEmail('');
      setPassword('');
      showToast('Login successful! Welcome back, Admin.', 'success');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    setAdmin(false);
    setShowForm(false);
    setEditingPost(null);
    showToast('Logged out successfully.', 'info');
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setExcerpt('');
    setCategory('');
    setTags('');
    setFeaturedImageFile(null);
    setFeaturedImagePreview('');
    setAdditionalImages([]);
    setAdditionalImagePreviews([]);
    setWordCount(0);
    setShowForm(false);
    setEditingPost(null);
    setFormError('');
    setPreviewMode(false);
    setUploadProgress(0);
  };

  // Image upload functions
  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeaturedImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAdditionalImages(files);
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setAdditionalImagePreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const uploadImages = async (featuredFile, additionalFiles) => {
    const uploadedUrls = [];
    let totalFiles = 1 + (additionalFiles ? additionalFiles.length : 0);
    let uploadedCount = 0;

    // Upload featured image
    if (featuredFile) {
      const fileExt = featuredFile.name.split('.').pop();
      const fileName = `featured_${Date.now()}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;

      const { error } = await supabase.storage
        .from('blog-images')
        .upload(filePath, featuredFile);

      if (error) {
        console.error('Featured image upload error:', error);
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      uploadedUrls.push(publicUrl);
      uploadedCount++;
      setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
    }

    // Upload additional images
    if (additionalFiles && additionalFiles.length > 0) {
      for (const file of additionalFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `image_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        const filePath = `blog-images/${fileName}`;

        const { error } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file);

        if (error) {
          console.error('Additional image upload error:', error);
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setIsPublishing(true);
    setUploadProgress(0);

    if (!title.trim() || !content.trim()) {
      setFormError('Please fill in title and content.');
      setFormLoading(false);
      setIsPublishing(false);
      return;
    }

    try {
      // Upload images first
      let imageUrls = [];
      if (featuredImageFile || additionalImages.length > 0) {
        imageUrls = await uploadImages(featuredImageFile, additionalImages);
      }

const postData = {
  title: title.trim(),
  excerpt: excerpt.trim() || content.trim().substring(0, 200) + '...',
  content: content.trim(),
  category: category.trim() || 'AICIS Breaking News',
  images: imageUrls,
  tags: tags.trim() ? tags.split(',').map(tag => tag.trim()) : [], // Ensure array
  date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  author: 'ABVIG Official',
  word_count: wordCount
};

      if (editingPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPost.id);
        if (error) throw error;
        showToast('Post updated successfully!', 'success');
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);
        if (error) throw error;
        showToast('Post published successfully!', 'success');
      }

      resetForm();
      fetchPosts();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setFormLoading(false);
      setIsPublishing(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.content);
    setExcerpt(post.excerpt || '');
    setCategory(post.category || '');
    setTags(post.tags ? post.tags.join(', ') : '');
    setWordCount(post.word_count || 0);
    setFeaturedImageFile(null);
    setFeaturedImagePreview(post.images && post.images.length > 0 ? post.images[0] : '');
    setAdditionalImages([]);
    setAdditionalImagePreviews(post.images && post.images.length > 1 ? post.images.slice(1) : []);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', deleteId);
      if (error) throw error;
      fetchPosts();
      showToast('Post deleted successfully.', 'info');
    } catch (error) {
      showToast('Error deleting post: ' + error.message, 'error');
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const togglePostExpand = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
  };

  const showToast = (message, type = 'info') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Share functions
  const shareOnFacebook = (post) => {
    const url = `https://abvig.com/blog/${post.id}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTwitter = (post) => {
    const url = `https://abvig.com/blog/${post.id}`;
    const text = encodeURIComponent(`${post.title} - Read more on ABVIG Blog`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = (post) => {
    const url = `https://abvig.com/blog/${post.id}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnWhatsApp = (post) => {
    const url = `https://abvig.com/blog/${post.id}`;
    const text = `${post.title} - Read more on ABVIG Blog`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
  };

  const copyLink = (post) => {
    const url = `https://abvig.com/blog/${post.id}`;
    navigator.clipboard.writeText(url);
    setSharePopup(post.id);
    setTimeout(() => setSharePopup(null), 2000);
    showToast('Link copied to clipboard!', 'success');
  };

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeFilter === 'all' || post.category === activeFilter;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['all', ...new Set(posts.map(post => post.category).filter(Boolean))];

  return (
    <div className="blog-dashboard">
      {/* Toast Notification */}
      {toastMessage && (
        <div className={`toast toast-${toastMessage.type}`}>
          <span>{toastMessage.message}</span>
          <button onClick={() => setToastMessage(null)}>×</button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h3>Delete Post</h3>
            <p>Are you sure you want to delete this post? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="modal-confirm" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-content">
          <span className="blog-badge">ABVIG Blog</span>
          <h1>Insights & Updates</h1>
          <p>Stories, news, and perspectives from the forefront of Africa's transformation</p>
        </div>
      </section>

      {/* Admin Panel */}
      <section className="blog-admin-section">
        <div className="container">
          <div className="admin-panel">
            {!admin ? (
              <div className="admin-login">
                <button className="btn-gold login-trigger" onClick={() => setShowLogin(!showLogin)}>
                  {showLogin ? '✕ Close' : '🔐 Admin Login'}
                </button>
                {showLogin && (
                  <div className="login-card">
                    <div className="login-header">
                      <span className="login-icon">👤</span>
                      <h3>Administrator Access</h3>
                      <p>Enter your credentials to manage blog content</p>
                    </div>
                    <form onSubmit={handleLogin}>
                      <div className="form-group">
                        <label>Email Address</label>
                        <input
                          type="email"
                          placeholder="admin@abvig.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                      <div className="login-hint">
                        <span></span>
                      </div>
                      {authError && <p className="error-text">{authError}</p>}
                      <button type="submit" className="btn-gold login-btn" disabled={authLoading}>
                        {authLoading ? 'Authenticating...' : 'Sign In'}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            ) : (
              <div className="admin-controls">
                <div className="admin-welcome">
                  <span className="admin-avatar">👨‍💻</span>
                  <span className="admin-name">Administrator</span>
                  <span className="admin-badge">Active</span>
                </div>
                <div className="admin-actions-bar">
                  <button className="btn-gold btn-new-post" onClick={() => {
                    if (showForm) {
                      resetForm();
                    } else {
                      setShowForm(true);
                      setEditingPost(null);
                      setTitle('');
                      setContent('');
                      setExcerpt('');
                      setCategory('');
                      setTags('');
                      setFeaturedImageFile(null);
                      setFeaturedImagePreview('');
                      setAdditionalImages([]);
                      setAdditionalImagePreviews([]);
                      setWordCount(0);
                      setPreviewMode(false);
                      setUploadProgress(0);
                    }
                  }}>
                    {showForm ? '✕ Cancel' : '➕ New Post'}
                  </button>
                  <button className="btn-outline-gold btn-logout" onClick={handleLogout}>
                    🚪 Logout
                  </button>
                </div>
              </div>
            )}

            {/* Full Post Editor */}
            {showForm && admin && (
              <div className="admin-form-container">
                <div className="form-header">
                  <div className="form-header-left">
                    <h3>{editingPost ? '✏️ Edit Post' : '📝 Create New Post'}</h3>
                    <span className={`form-badge ${editingPost ? 'editing' : 'draft'}`}>
                      {editingPost ? 'Editing' : 'Draft'}
                    </span>
                  </div>
                  <div className="form-header-right">
                    <span className="word-count">{wordCount} words</span>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <span className="upload-progress-text">{uploadProgress}%</span>
                    )}
                    <button 
                      type="button" 
                      className="preview-toggle"
                      onClick={() => setPreviewMode(!previewMode)}
                    >
                      {previewMode ? '✕ Close Preview' : '👁️ Preview'}
                    </button>
                  </div>
                </div>

                {previewMode ? (
                  <div className="preview-container">
                    <div className="preview-header">
                      <h2>{title || 'Your Title Here'}</h2>
                      {featuredImagePreview && (
                        <div className="preview-featured-image">
                          <img src={featuredImagePreview} alt={title} />
                        </div>
                      )}
                      <div className="preview-meta">
                        <span>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        <span>{category || 'Uncategorized'}</span>
                      </div>
                    </div>
                    <div className="preview-content">
                      {content ? (
                        <div dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }} />
                      ) : (
                        <p className="preview-placeholder">Your content will appear here...</p>
                      )}
                    </div>
                    <div className="preview-footer">
                      <button className="btn-outline-gold" onClick={() => setPreviewMode(false)}>
                        Back to Editor
                      </button>
                    </div>
                  </div>
                ) : (
                  <form className="admin-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Post Title *</label>
                        <input
                          type="text"
                          placeholder="Enter compelling title (e.g., 'AICIS 2026: Shaping Africa's Climate Future')"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          required
                        />
                        <span className="field-hint">Titles should be descriptive and under 60 characters for best SEO</span>
                      </div>
                    </div>

                    <div className="form-row two-col">
                      <div className="form-group">
                        <label>Category</label>
                        <input
                          type="text"
                          placeholder="e.g., AICIS, Climate Change, Investment"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          list="categories"
                        />
                        <datalist id="categories">
                          <option value="AICIS Breaking News" />
                          <option value="Climate Change" />
                          <option value="Green Investment" />
                          <option value="Infrastructure" />
                          <option value="Energy" />
                          <option value="Trade" />
                        </datalist>
                      </div>
                      <div className="form-group">
                        <label>Tags</label>
                        <input
                          type="text"
                          placeholder="AICIS, Climate, Investment (comma separated)"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Excerpt (Optional)</label>
                      <textarea
                        placeholder="Write a brief summary of your post (120-160 characters recommended for SEO)"
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        rows="2"
                      />
                      <span className="field-hint">{excerpt.length} characters - Recommended: 120-160</span>
                    </div>

                    <div className="form-group">
                      <label>Content *</label>
                      <textarea
                        ref={contentRef}
                        placeholder="Write your post content here..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows="12"
                        required
                      />
                      <div className="editor-toolbar">
                        <button type="button" onClick={() => {
                          const textarea = contentRef.current;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const before = content.substring(0, start);
                          const after = content.substring(end);
                          setContent(before + '**' + content.substring(start, end) + '**' + after);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.selectionStart = start + 2;
                            textarea.selectionEnd = end + 2;
                          }, 10);
                        }} title="Bold">B</button>
                        <button type="button" onClick={() => {
                          const textarea = contentRef.current;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const before = content.substring(0, start);
                          const after = content.substring(end);
                          setContent(before + '* ' + content.substring(start, end) + after);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.selectionStart = start + 2;
                            textarea.selectionEnd = end + 2;
                          }, 10);
                        }} title="Bullet Point">•</button>
                        <button type="button" onClick={() => {
                          const textarea = contentRef.current;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const before = content.substring(0, start);
                          const after = content.substring(end);
                          setContent(before + '### ' + content.substring(start, end) + after);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.selectionStart = start + 4;
                            textarea.selectionEnd = end + 4;
                          }, 10);
                        }} title="Heading">H3</button>
                        <button type="button" onClick={() => {
                          const textarea = contentRef.current;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const before = content.substring(0, start);
                          const after = content.substring(end);
                          setContent(before + '[Link](url)' + after);
                          setTimeout(() => {
                            textarea.focus();
                            textarea.selectionStart = start + 6;
                            textarea.selectionEnd = end + 6;
                          }, 10);
                        }} title="Link">🔗</button>
                        <span className="toolbar-hint">Formatting: Bold, Lists, Headings</span>
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    <div className="image-upload-section">
                      <div className="form-row two-col">
                        <div className="form-group">
                          <label>Featured Image</label>
                          <div className="file-upload-wrapper">
                            <input
                              type="file"
                              ref={featuredInputRef}
                              accept="image/*"
                              onChange={handleFeaturedImageChange}
                              className="file-input-hidden"
                            />
                            <button 
                              type="button" 
                              className="file-upload-btn"
                              onClick={() => featuredInputRef.current.click()}
                            >
                              📁 Choose Featured Image
                            </button>
                            {featuredImageFile && (
                              <span className="file-name">{featuredImageFile.name}</span>
                            )}
                          </div>
                          {featuredImagePreview && (
                            <div className="image-preview">
                              <img src={featuredImagePreview} alt="Featured" />
                              <button 
                                type="button" 
                                className="remove-image"
                                onClick={() => {
                                  setFeaturedImageFile(null);
                                  setFeaturedImagePreview('');
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Additional Images</label>
                          <div className="file-upload-wrapper">
                            <input
                              type="file"
                              ref={additionalInputRef}
                              accept="image/*"
                              multiple
                              onChange={handleAdditionalImagesChange}
                              className="file-input-hidden"
                            />
                            <button 
                              type="button" 
                              className="file-upload-btn"
                              onClick={() => additionalInputRef.current.click()}
                            >
                              📁 Choose Additional Images
                            </button>
                            {additionalImages.length > 0 && (
                              <span className="file-name">{additionalImages.length} files selected</span>
                            )}
                          </div>
                          {additionalImagePreviews.length > 0 && (
                            <div className="additional-images-preview">
                              {additionalImagePreviews.map((preview, index) => (
                                <div className="additional-image-item" key={index}>
                                  <img src={preview} alt={`Additional ${index + 1}`} />
                                  <button 
                                    type="button" 
                                    className="remove-image"
                                    onClick={() => {
                                      const newImages = [...additionalImages];
                                      const newPreviews = [...additionalImagePreviews];
                                      newImages.splice(index, 1);
                                      newPreviews.splice(index, 1);
                                      setAdditionalImages(newImages);
                                      setAdditionalImagePreviews(newPreviews);
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="upload-progress-bar">
                        <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                        <span>{uploadProgress}%</span>
                      </div>
                    )}

                    {formError && <p className="error-text">{formError}</p>}

                    <div className="form-actions">
                      <button type="submit" className="btn-gold publish-btn" disabled={formLoading}>
                        {isPublishing ? (
                          <>
                            <span className="spinner"></span>
                            Publishing...
                          </>
                        ) : editingPost ? (
                          '✏️ Update Post'
                        ) : (
                          '📤 Publish Post'
                        )}
                      </button>
                      <button type="button" className="btn-outline-gold" onClick={resetForm}>
                        Cancel
                      </button>
                      <button type="button" className="btn-outline-gold preview-btn" onClick={() => setPreviewMode(true)}>
                        👁️ Preview
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="blog-posts-section">
        <div className="container">
          <div className="blog-toolbar">
            <div className="filter-tabs">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`filter-tab ${activeFilter === cat ? 'active' : ''}`}
                  onClick={() => setActiveFilter(cat)}
                >
                  {cat === 'all' ? '📋 All' : cat}
                </button>
              ))}
            </div>
            <div className="search-bar">
              <input
                type="text"
                placeholder="🔍 Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="post-count">{filteredPosts.length} posts</span>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <h3>No posts found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="blog-grid">
              {filteredPosts.map((post) => (
                <article className={`blog-card ${expandedPost === post.id ? 'expanded' : ''}`} key={post.id}>
                  {post.images && post.images.length > 0 && (
                    <div 
                      className="blog-card-image" 
                      style={{ 
                        backgroundImage: `url(${post.images[0]})`,
                      }}
                    >
                      {post.images.length > 1 && (
                        <span className="image-count">+{post.images.length - 1} more</span>
                      )}
                    </div>
                  )}
                  <div className="blog-card-content">
                    <div className="blog-meta">
                      <span className="blog-category">{post.category || 'AICIS Breaking News'}</span>
                      <span className="blog-date">{post.date}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p className="blog-excerpt">
                      {expandedPost === post.id 
                        ? post.content 
                        : post.excerpt || post.content.substring(0, 150) + '...'}
                    </p>
                    {post.content.length > 150 && (
                      <button 
                        className="expand-btn" 
                        onClick={() => togglePostExpand(post.id)}
                      >
                        {expandedPost === post.id ? 'Read Less' : 'Read More'}
                      </button>
                    )}
                    <div className="blog-card-footer">
                      <span className="blog-author">✍️ {post.author || 'ABVIG Official'}</span>
                      <div className="blog-actions">
                        <button className="share-btn" onClick={() => shareOnFacebook(post)} title="Share on Facebook">📘</button>
                        <button className="share-btn" onClick={() => shareOnTwitter(post)} title="Share on Twitter">🐦</button>
                        <button className="share-btn" onClick={() => shareOnLinkedIn(post)} title="Share on LinkedIn">🔗</button>
                        <button className="share-btn" onClick={() => shareOnWhatsApp(post)} title="Share on WhatsApp">💬</button>
                        <button className="share-btn copy-btn" onClick={() => copyLink(post)} title="Copy link">📋</button>
                        {sharePopup === post.id && <span className="copy-tooltip">✓ Copied!</span>}
                      </div>
                    </div>
                    {admin && (
                      <div className="admin-actions">
                        <button className="edit-btn" onClick={() => handleEdit(post)}>✎ Edit</button>
                        <button className="delete-btn" onClick={() => confirmDelete(post.id)}>✕ Delete</button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;